import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./LandingPage.css";
import EarlyAccessForm from "./EarlyAccessForm";
import logo from "../assets/logo.jpg";
import SEOHead from "./SEOHead";

const ComlinkrLandingPage = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [showFAQ, setShowFAQ] = useState({});
  const navigate = useNavigate();

  // 4 core pillars — drawn from actual mobile app tabs
  const pillars = [
    {
      icon: "🏠",
      title: "Housing",
      subtitle: "Find & Compare",
      desc: "Culturally-matched properties, compatible roommates, and neighbourhood intelligence — filtered by lifestyle and values, not just price.",
      color: "#1E88E5",
      bg: "rgba(30,136,229,0.07)",
    },
    {
      icon: "🍽️",
      title: "Food",
      subtitle: "Discover & Explore",
      desc: "Restaurants, cafés, and markets matched to your cuisine preferences — Italian, Indian, Thai, Chinese, and more, wherever you are.",
      color: "#FF9800",
      bg: "rgba(255,152,0,0.07)",
    },
    {
      icon: "👥",
      title: "Community",
      subtitle: "Connect & Ask",
      desc: "Local Q&A and tips across Housing, Food, Transit, Safety, Nightlife, Pets, and Events — connect with locals and expats who know.",
      color: "#4CAF50",
      bg: "rgba(76,175,80,0.07)",
    },
    {
      icon: "📰",
      title: "Feed",
      subtitle: "Stay Informed",
      desc: "Local and global news, community events (Community, Food, Arts, Tech, Wellness), popular city guides, and social posts — all in one place.",
      color: "#9C27B0",
      bg: "rgba(156,39,176,0.07)",
    },
  ];

  const features = [
    { icon: "🏠", title: "Smart Property Discovery", desc: "Matching based on lifestyle, culture, and preferences" },
    { icon: "🤝", title: "Intelligent Roommate Matching", desc: "Find compatible living partners through deep compatibility analysis" },
    { icon: "🍽️", title: "Cultural Food Hub", desc: "Discover restaurants and markets that feel familiar and exciting" },
    { icon: "📍", title: "Neighbourhood Intelligence", desc: "Know what surrounds you before you move — culture, services, community" },
    { icon: "🌍", title: "Local Community Connection", desc: "Connect with events, groups, and experiences in your area" },
    { icon: "📰", title: "Local & Global News Feed", desc: "Stay updated with news by city, country, and category — your city, your world" },
  ];

  const sections = [
    { name: "Mission", path: "/mission", icon: "🎯", description: "Bridging people to belonging, not just places" },
    { name: "Vision", path: "/vision", icon: "🔮", description: "A world where everyone finds their perfect community" },
    {
      name: "Team & Founders",
      path: "/team",
      icon: "👥",
      description: "Founders: Pavan Rao, Manav Trivedi. Team: Kush Patel, Mrunal Shah, Jainil Mathia, Fenil Mahesuriya.",
    },
  ];

  const personas = [
    {
      icon: "✈️",
      scenario: "The International Mover",
      persona: "Priya, relocating from India to London",
      challenge: "Wants familiar food, a culturally compatible roommate, and a community she can relate to",
      solution: "ComLinkr finds her a matched home, nearby Indian restaurants, and a local community group — before she even lands",
    },
    {
      icon: "🎓",
      scenario: "The Graduate Student",
      persona: "Joe, starting PhD in the UK",
      challenge: "Needs a quiet study environment with people who understand academic lifestyle",
      solution: "ComLinkr connects him with research-focused flatmates in academic-friendly neighbourhoods",
    },
    {
      icon: "🏘️",
      scenario: "The Local Explorer",
      persona: "Maria, 3-year Berlin resident",
      challenge: "Feels disconnected — wants to discover hidden cultural gems, local events, and new communities in her own city",
      solution: "ComLinkr surfaces neighbourhood events, community groups, new restaurants, and local news she was missing all along",
    },
    {
      icon: "💻",
      scenario: "The Remote Worker",
      persona: "Sofia, digital nomad moving cities every few months",
      challenge: "Needs a peaceful workspace, cultural amenities, and to quickly build a social life in each new city",
      solution: "ComLinkr matches her with remote-worker flatmates, cultural hotspots, and local events tailored to her city",
    },
  ];

  const faqs = [
    {
      q: "How does ComLinkr work?",
      a: "ComLinkr analyses your preferences — culture, lifestyle, dietary habits, social style — to match you with ideal properties, roommates, food spots, local news, and community groups. Create a profile and start discovering.",
    },
    {
      q: "Is ComLinkr only for people moving abroad?",
      a: "No. It serves everyone — students, professionals, families, and even locals who want to explore their own city better. If you want to feel more connected to where you live, ComLinkr is for you.",
    },
    {
      q: "What are ComLinkr's four main features?",
      a: "Housing (culturally-matched properties and roommates), Food (restaurant and cuisine discovery), Community (local Q&A and connections), and Feed (local news, events, city guides, and social posts).",
    },
    {
      q: "What kind of news and events are in the Feed?",
      a: "The Feed includes local, national, and global news across categories like Business, Technology, Health, Sports, and Entertainment — plus community events like food festivals, tech meetups, outdoor markets, wellness classes, and more.",
    },
    {
      q: "How do you ensure safety and authenticity?",
      a: "Verified profiles, community ratings, and authenticity checks help keep the platform trustworthy and safe for everyone.",
    },
    {
      q: "What makes ComLinkr different from Rightmove or Zoopla?",
      a: "We go beyond listings. ComLinkr matches on cultural compatibility, lifestyle values, food preferences, and community connections — so you don't just find a flat, you find a place you actually belong.",
    },
  ];

  const stats = [
    { number: "1M+", label: "Target Users" },
    { number: "50+", label: "Cities Planned" },
    { number: "95%", label: "Match Success Rate" },
    { number: "4", label: "Core Features" },
  ];

  const howItWorks = [
    {
      step: "01",
      icon: "📝",
      title: "Create Your Profile",
      desc: "Tell us your culture, lifestyle, dietary habits, work style, and what makes a place feel like home.",
    },
    {
      step: "02",
      icon: "🎯",
      title: "Get Matched",
      desc: "ComLinkr matches you with compatible housing, roommates, food spots, community groups, and local news in your city.",
    },
    {
      step: "03",
      icon: "🌍",
      title: "Belong Anywhere",
      desc: "Move in, explore, and connect. Discover events, cultural spots, local stories — and feel at home from day one.",
    },
  ];

  // Feed preview data — mirrors actual mobile Feed tab
  const feedEvents = [
    { icon: "🍽️", title: "Food & Wine Festival", date: "Apr 12", location: "Harbour Plaza", cat: "Food", price: "£25", attendees: 589 },
    { icon: "🎨", title: "Local Artists Market", date: "Mar 29", location: "Downtown Square", cat: "Arts", price: "Free", attendees: 156 },
    { icon: "💻", title: "Tech Meetup: AI & Future", date: "Apr 5", location: "Innovation Hub", cat: "Tech", price: "£15", attendees: 342 },
  ];

  const newsCategories = [
    { emoji: "📍", label: "Local" },
    { emoji: "🌍", label: "World" },
    { emoji: "💼", label: "Business" },
    { emoji: "💻", label: "Technology" },
    { emoji: "🏥", label: "Health" },
    { emoji: "⚽", label: "Sports" },
    { emoji: "🎬", label: "Entertainment" },
  ];

  const popularCities = [
    { city: "London", emoji: "🇬🇧" },
    { city: "New York", emoji: "🗽" },
    { city: "Dubai", emoji: "🇦🇪" },
    { city: "Toronto", emoji: "🇨🇦" },
    { city: "Sydney", emoji: "🇦🇺" },
    { city: "Mumbai", emoji: "🇮🇳" },
  ];

  useEffect(() => {
    const interval = setInterval(() => setActiveFeature((p) => (p + 1) % features.length), 4000);
    return () => clearInterval(interval);
  }, [features.length]);

  const toggleFAQ = (i) => setShowFAQ((prev) => ({ ...prev, [i]: !prev[i] }));

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="landing-container">
      <SEOHead
        title="ComLinkr — Find Housing, Food & Community Where You Belong"
        description="ComLinkr connects you to housing, local food, community events, and a live feed — all in one app. Built for international movers, students, and local explorers worldwide."
        canonical="https://www.comlinkr.com/"
      />

      {/* ── HERO ── */}
      <section className="hero-section hero-centered" id="hero">
        <div className="container">
          <div className="hero-center-content">
            <motion.img
              src={logo}
              alt="ComLinkr Logo"
              className="hero-logo"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 1.2, type: "spring" }}
            />

            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              <div className="launch-banner">
                <span className="launch-dot" />
                <span className="launch-label">App Launching Soon</span>
                <span className="launch-badge">2026</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <h1 className="hero-title">
                Housing is just the beginning.<br />
                <span className="brand-highlight">Find where you belong.</span>
              </h1>
              <p className="hero-subtitle">
                ComLinkr is the all-in-one platform to find <strong>compatible housing</strong>,
                discover <strong>cultural food spots</strong>, connect with your <strong>local community</strong>,
                and stay informed — whether you're moving cities or exploring your own.
              </p>

              <div className="hero-cta-group">
                <a
                  href="#join-early"
                  className="btn btn-primary btn-hero"
                  onClick={(e) => scrollToSection(e, "#join-early")}
                >
                  Get Early Access
                </a>
                <a
                  href="#about"
                  className="btn btn-outline-hero"
                  onClick={(e) => scrollToSection(e, "#about")}
                >
                  How It Works ↓
                </a>
              </div>
            </motion.div>

            {/* 4 pillars strip */}
            <motion.div
              className="hero-pillars"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.55 }}
            >
              {pillars.map((p, i) => (
                <div key={i} className="hero-pillar-chip" style={{ borderColor: `${p.color}22`, background: p.bg }}>
                  <span className="pillar-chip-icon">{p.icon}</span>
                  <span className="pillar-chip-label" style={{ color: p.color }}>{p.title}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHAT IS COMLINKR ── */}
      <section className="about-section" id="about">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">What is ComLinkr?</span>
            <h2 className="section-title">
              More than an app — <span style={{ color: "var(--primary)" }}>a way to belong</span>
            </h2>
            <p className="section-lead">
              Whether you're moving to a new city, relocating across the world, or just a local who wants to
              discover more — ComLinkr connects you to housing, food, people, and stories that match your culture and lifestyle.
            </p>
          </motion.div>

          {/* Problem → Solution */}
          <div className="row align-items-stretch mb-5 g-4">
            <div className="col-lg-5">
              <motion.div
                className="problem-card"
                initial={{ x: -40, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
              >
                <h3 className="problem-title">😔 The problem</h3>
                <ul className="problem-list">
                  <li>
                    <span>🏚️</span>
                    <div><strong>Housing platforms ignore culture</strong> — listings focus on price and square footage, not lifestyle compatibility.</div>
                  </li>
                  <li>
                    <span>🤷</span>
                    <div><strong>Roommate roulette is real</strong> — finding someone who shares your habits, values, and background is left to luck.</div>
                  </li>
                  <li>
                    <span>😶‍🌫️</span>
                    <div><strong>New cities feel isolating</strong> — you don't know where your community is, where to eat, or what's happening locally.</div>
                  </li>
                </ul>
              </motion.div>
            </div>

            <div className="col-lg-2 d-flex align-items-center justify-content-center">
              <motion.div
                className="arrow-divider"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
              >→</motion.div>
            </div>

            <div className="col-lg-5">
              <motion.div
                className="solution-card"
                initial={{ x: 40, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
              >
                <h3 className="solution-title">✨ ComLinkr's answer</h3>
                <ul className="solution-list">
                  <li>
                    <span>🏠</span>
                    <div><strong>Culturally-matched housing</strong> — properties and roommates filtered by lifestyle, values, and cultural background.</div>
                  </li>
                  <li>
                    <span>🍽️</span>
                    <div><strong>Food that feels familiar</strong> — discover restaurants and cuisines matched to what you love and who you are.</div>
                  </li>
                  <li>
                    <span>🌍</span>
                    <div><strong>Your community, found</strong> — local Q&A, news, events, and social connections — all personalised to your city.</div>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>

          {/* 4 Pillars — HORIZONTAL CARD LAYOUT (2x2 grid, no overflow) */}
          <div className="row g-4">
            {pillars.map((p, i) => (
              <div key={i} className="col-lg-6">
                <motion.div
                  className="pillar-card-h"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  whileHover={{ y: -5, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" }}
                  style={{ "--pillar-color": p.color, "--pillar-bg": p.bg }}
                >
                  <div className="pillar-h-icon">{p.icon}</div>
                  <div className="pillar-h-body">
                    <div className="pillar-h-header">
                      <h4 className="pillar-h-title">{p.title}</h4>
                      <span className="pillar-h-sub">{p.subtitle}</span>
                    </div>
                    <p className="pillar-h-desc">{p.desc}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="how-it-works-section" id="how-it-works">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Simple &amp; Powerful</span>
            <h2 className="section-title">How ComLinkr Works</h2>
          </motion.div>

          <div className="row g-4 justify-content-center">
            {howItWorks.map((step, i) => (
              <div key={i} className="col-lg-4 col-md-6">
                <motion.div
                  className="step-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15 }}
                >
                  <div className="step-number">{step.step}</div>
                  <div className="step-icon-wrap">{step.icon}</div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-desc">{step.desc}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES SHOWCASE ── */}
      <section className="features-showcase" id="features">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Under the Hood</span>
            <h2 className="section-title">What Powers ComLinkr</h2>
          </motion.div>

          <div className="features-container">
            <div className="row">
              <div className="col-lg-8">
                <div className="features-display">
                  <motion.div
                    key={activeFeature}
                    className="active-feature"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="feature-icon">{features[activeFeature].icon}</div>
                    <h3>{features[activeFeature].title}</h3>
                    <p>{features[activeFeature].desc}</p>
                  </motion.div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="features-list">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className={`feature-item ${activeFeature === index ? "active" : ""}`}
                      onClick={() => setActiveFeature(index)}
                      whileHover={{ scale: 1.04 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <span className="feature-number">{index + 1}</span>
                      <div className="feature-info">
                        <span className="feature-emoji">{feature.icon}</span>
                        <span className="feature-name">{feature.title}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section className="personas-section" id="community">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">For Everyone, Everywhere</span>
            <h2 className="section-title">Who ComLinkr Is For</h2>
            <p className="section-lead">
              Whether you're crossing borders or just exploring your own neighbourhood — ComLinkr helps you build a life, not just find a lease.
            </p>
          </motion.div>

          <div className="row g-4">
            {personas.map((t, i) => (
              <div key={i} className="col-lg-6">
                <motion.div
                  className="persona-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="persona-header">
                    <span className="persona-icon">{t.icon}</span>
                    <div>
                      <h5 className="persona-scenario">{t.scenario}</h5>
                      <span className="persona-name">{t.persona}</span>
                    </div>
                  </div>
                  <div className="persona-body">
                    <div className="persona-challenge">
                      <span className="persona-label">Challenge</span>
                      <p>{t.challenge}</p>
                    </div>
                    <div className="persona-solution">
                      <span className="persona-label solution">ComLinkr's Answer</span>
                      <p>{t.solution}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="stats-section">
        <div className="container">
          <motion.h2
            className="stats-heading text-center"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Mission Goals
          </motion.h2>
          <div className="row">
            {stats.map((stat, index) => (
              <div key={index} className="col-lg-3 col-md-6 mb-4">
                <motion.div
                  className="stat-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEED PREVIEW — shows actual richness of the Feed tab ── */}
      <section className="news-feed-section" id="feed">
        <div className="container">
          <motion.div
            className="text-center mb-4"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Live on ComLinkr</span>
            <h2 className="section-title">Your Feed, Your City</h2>
            <p className="section-lead">
              The ComLinkr Feed brings you local news, community events, popular city guides, and social posts — all personalised to where you are and what you care about.
            </p>
          </motion.div>

          {/* News Categories */}
          <div className="feed-block">
            <div className="feed-block-header">
              <span className="feed-block-title">📰 News Categories</span>
              <span className="feed-block-badge">Personalised by city &amp; country</span>
            </div>
            <div className="news-cats">
              {newsCategories.map((c, i) => (
                <span key={i} className="news-cat-pill">{c.emoji} {c.label}</span>
              ))}
            </div>
          </div>

          {/* Events Preview */}
          <div className="feed-block">
            <div className="feed-block-header">
              <span className="feed-block-title">🎉 Upcoming Events</span>
              <span className="feed-block-badge">Community · Food · Arts · Tech · Wellness</span>
            </div>
            <div className="row g-3">
              {feedEvents.map((ev, i) => (
                <div key={i} className="col-lg-4 col-md-6">
                  <motion.div
                    className="event-preview-card"
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="event-icon-wrap">{ev.icon}</div>
                    <div className="event-body">
                      <div className="event-top">
                        <span className="event-cat">{ev.cat}</span>
                        <span className={`event-price ${ev.price === "Free" ? "free" : ""}`}>{ev.price}</span>
                      </div>
                      <h6 className="event-title">{ev.title}</h6>
                      <p className="event-meta">📅 {ev.date} &nbsp;·&nbsp; 📍 {ev.location}</p>
                      <p className="event-interested">⭐ {ev.attendees} interested</p>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Cities */}
          <div className="feed-block">
            <div className="feed-block-header">
              <span className="feed-block-title">🏙️ Popular Cities</span>
              <span className="feed-block-badge">Browse by city guide</span>
            </div>
            <div className="city-pills">
              {popularCities.map((c, i) => (
                <span key={i} className="city-pill">{c.emoji} {c.city}</span>
              ))}
            </div>
          </div>

          {/* Community Posts */}
          <div className="feed-block">
            <div className="feed-block-header">
              <span className="feed-block-title">💬 Community Posts</span>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <motion.div className="feed-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                  <div className="feed-header">
                    <strong>Navratri Night 2025</strong>
                    <span className="feed-tag feed-tag-event">Event</span>
                  </div>
                  <p className="mb-1"><small>Oct 4 • Business Park Club</small></p>
                  <p className="mb-2">Join the community celebration. <a href="#hashtag">#Navratri</a> <a href="#hashtag">#Meetups</a></p>
                  <div className="feed-meta"><span>❤️ 30</span><span className="ms-3">💬 8</span></div>
                </motion.div>
              </div>
              <div className="col-md-6">
                <motion.div className="feed-card" whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                  <div className="feed-header">
                    <strong>Shreya — Room Available</strong>
                    <span className="feed-tag feed-tag-housing">Housing</span>
                  </div>
                  <p className="mb-1"><small>No smoking • Hygienic • Move-in Aug</small></p>
                  <p className="mb-2">East London • 0.2 mi • See details.</p>
                  <div className="feed-meta"><span>🏠 Housing</span></div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EARLY ACCESS CTA ── */}
      <section className="join-section" id="join-early">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-10">
              <motion.div
                className="join-card"
                initial={{ y: 40, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
              >
                <div className="join-badge">Early Access</div>
                <h2 className="join-title">Be the first to experience ComLinkr</h2>
                <p className="join-subtitle">
                  Sign up for exclusive early access, stay updated, and help shape the platform with your feedback.
                </p>

                <div className="join-why">
                  <div className="join-why-item"><span>✓</span> Access beta features before public launch</div>
                  <div className="join-why-item"><span>✓</span> Be part of shaping ComLinkr through your feedback</div>
                  <div className="join-why-item"><span>✓</span> Get community-driven updates and city stories</div>
                </div>

                <EarlyAccessForm />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DISCOVER MORE ── */}
      <section className="navigation-section" id="mission">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Our Story</span>
            <h2 className="section-title">Discover More About ComLinkr</h2>
          </motion.div>

          <div className="row">
            {sections.map((section, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <motion.div
                  className="nav-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -10, boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="nav-icon">{section.icon}</div>
                  <h4>{section.name}</h4>
                  <p>{section.description}</p>
                  <button className="btn btn-outline-primary btn-sm" onClick={() => navigate(section.path)}>
                    Explore {section.name}
                  </button>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="faq-section" id="faq">
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            <span className="section-eyebrow">Got Questions?</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
          </motion.div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="faq-item"
                  initial={{ y: 30, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                >
                  <div className="faq-question" onClick={() => toggleFAQ(index)}>
                    <h5>{faq.q}</h5>
                    <span className={`faq-toggle ${showFAQ[index] ? "active" : ""}`}>+</span>
                  </div>
                  <motion.div
                    className="faq-answer"
                    initial={false}
                    animate={{ height: showFAQ[index] ? "auto" : 0, opacity: showFAQ[index] ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p>{faq.a}</p>
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ComlinkrLandingPage;
